import React, { useEffect, useState } from 'react';
import {
  Badge,
  Box,
  Divider,
  IconButton,
  ListItemText,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
} from '@mui/material';
import NotificationsIcon from '@mui/icons-material/Notifications';
import { formatDistanceToNow } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '../../store';
import {
  fetchNotifications,
  markAllNotificationsAsRead,
  markNotificationAsRead,
} from '../../store/slices/notificationsSlice';
import { AppNotification } from '../../services/notificationsService';

const POLL_INTERVAL_MS = 60000;

const NotificationsMenu: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { user } = useAppSelector((state) => state.auth);
  const { notifications, unreadCount } = useAppSelector((state) => state.notifications);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  useEffect(() => {
    if (!user?.user_id) return;
    dispatch(fetchNotifications(user.user_id));
    const interval = setInterval(() => {
      dispatch(fetchNotifications(user.user_id));
    }, POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [user?.user_id, dispatch]);

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => setAnchorEl(event.currentTarget);
  const handleClose = () => setAnchorEl(null);

  const handleNotificationClick = (notification: AppNotification) => {
    if (!user?.user_id) return;
    if (!notification.read_at) {
      dispatch(markNotificationAsRead({ id: notification.id, userId: user.user_id }));
    }
    handleClose();
    if (notification.link) {
      navigate(notification.link);
    }
  };

  const handleMarkAllRead = () => {
    if (!user?.user_id) return;
    dispatch(markAllNotificationsAsRead(user.user_id));
  };

  return (
    <>
      <Tooltip title={t('notifications.title', 'Notifications')}>
        <IconButton onClick={handleOpen} sx={{ color: 'text.primary', mr: 1 }}>
          <Badge badgeContent={unreadCount} color="error" max={99}>
            <NotificationsIcon />
          </Badge>
        </IconButton>
      </Tooltip>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{ sx: { width: 380, maxHeight: 480 } }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, py: 1 }}>
          <Typography variant="subtitle1" fontWeight={600}>
            {t('notifications.title', 'Notifications')}
          </Typography>
          {unreadCount > 0 && (
            <Typography
              variant="body2"
              color="primary"
              sx={{ cursor: 'pointer' }}
              onClick={handleMarkAllRead}
            >
              {t('notifications.markAllRead', 'Mark all read')}
            </Typography>
          )}
        </Box>
        <Divider />
        {notifications.length === 0 ? (
          <Box sx={{ px: 2, py: 4, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              {t('notifications.empty', "You're all caught up")}
            </Typography>
          </Box>
        ) : (
          notifications.map((notification) => (
            <MenuItem
              key={notification.id}
              onClick={() => handleNotificationClick(notification)}
              sx={{
                whiteSpace: 'normal',
                alignItems: 'flex-start',
                py: 1.25,
                backgroundColor: notification.read_at ? 'transparent' : 'action.hover',
              }}
            >
              <ListItemText
                primary={
                  <Typography variant="body2" fontWeight={notification.read_at ? 400 : 600}>
                    {notification.title}
                  </Typography>
                }
                secondary={
                  <>
                    {notification.message && (
                      <Typography variant="body2" color="text.secondary" component="span" display="block" noWrap>
                        {notification.message}
                      </Typography>
                    )}
                    <Typography variant="caption" color="text.disabled">
                      {formatDistanceToNow(new Date(notification.created_at), { addSuffix: true })}
                    </Typography>
                  </>
                }
              />
            </MenuItem>
          ))
        )}
      </Menu>
    </>
  );
};

export default NotificationsMenu;
